import toast, { type DefaultToastOptions, type ToastOptions } from 'react-hot-toast';
import type { CSSProperties } from 'react';

function isDarkMode(): boolean {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark');
}

function baseStyle(): CSSProperties {
  const dark = isDarkMode();
  return {
    borderRadius: '0.75rem',
    border: dark ? '1px solid rgb(51 65 85)' : '1px solid rgb(226 232 240)',
    padding: '12px 16px',
    fontSize: '14px',
    color: dark ? 'rgb(241 245 249)' : 'rgb(15 23 42)',
    background: dark ? 'rgb(15 23 42)' : 'rgb(255 255 255)',
    boxShadow: dark
      ? '0 4px 6px -1px rgb(0 0 0 / 0.35)'
      : '0 4px 6px -1px rgb(0 0 0 / 0.06)',
    maxWidth: '420px',
  };
}

function successOptions(): ToastOptions {
  const dark = isDarkMode();
  return {
    duration: 3500,
    iconTheme: { primary: '#059669', secondary: '#fff' },
    style: {
      ...baseStyle(),
      borderColor: dark ? 'rgb(6 78 59)' : 'rgb(209 250 229)',
      background: dark ? 'rgb(2 44 34)' : 'rgb(236 253 245)',
      color: dark ? 'rgb(167 243 208)' : 'rgb(6 78 59)',
    },
  };
}

function errorOptions(): ToastOptions {
  const dark = isDarkMode();
  return {
    duration: 6000,
    iconTheme: { primary: '#dc2626', secondary: '#fff' },
    style: {
      ...baseStyle(),
      borderColor: dark ? 'rgb(127 29 29)' : 'rgb(254 226 226)',
      background: dark ? 'rgb(69 10 10)' : 'rgb(254 242 242)',
      color: dark ? 'rgb(252 165 165)' : 'rgb(127 29 29)',
    },
  };
}

/** Passed to `<Toaster toastOptions={...} />` in providers. */
export const toastDefaults: DefaultToastOptions = {
  duration: 4000,
  position: 'top-right',
  style: baseStyle(),
  success: successOptions(),
  error: errorOptions(),
};

/** Unified toast API — use instead of raw react-hot-toast in app code. */
export const notify = {
  success: (message: string, options?: ToastOptions) =>
    toast.success(message, { ...successOptions(), ...options }),
  error: (message: string, options?: ToastOptions) =>
    toast.error(message, { ...errorOptions(), ...options }),
  warning: (message: string, options?: ToastOptions) => {
    const dark = isDarkMode();
    return toast(message, {
      duration: 5000,
      icon: '⚠',
      style: {
        ...baseStyle(),
        borderColor: dark ? 'rgb(120 53 15)' : 'rgb(254 243 199)',
        background: dark ? 'rgb(69 26 3)' : 'rgb(255 251 235)',
        color: dark ? 'rgb(253 230 138)' : 'rgb(120 53 15)',
      },
      ...options,
    });
  },
  info: (message: string, options?: ToastOptions) => {
    const dark = isDarkMode();
    return toast(message, {
      icon: 'ℹ',
      style: {
        ...baseStyle(),
        borderColor: dark ? 'rgb(51 65 85)' : 'rgb(226 232 240)',
        background: dark ? 'rgb(30 41 59)' : 'rgb(248 250 252)',
        color: dark ? 'rgb(203 213 225)' : 'rgb(51 65 85)',
      },
      ...options,
    });
  },
  promise: toast.promise,
};
